import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation } from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatStepperModule } from '@angular/material/stepper'
import { ProjectorsFacade } from '@galabid/gb-dashboard/projector/data-access'
import { TranslateModule } from '@ngx-translate/core'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import * as ProjectorTemplatesActions from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.actions'
import { ProjectorTemplatesFacade } from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.facade'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { combineLatest, combineLatestWith, map, take } from 'rxjs'
import { GbPresenterTemplatesListComponent } from '../gb-presenter-templates-list/gb-presenter-templates-list.component'

type ApplyTemplateTarget = 'create' | 'existing'

interface SlideOption {
  uuid: string
  name: string
}

@Component({
  selector: 'gb-presenter-dialog-templates',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GbDashboardSharedUiModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatStepperModule,
    MatRadioModule,
    MatSelectModule,
    TranslateModule,
    GbPresenterTemplatesListComponent,
  ],
  templateUrl: './gb-presenter-dialog-templates.component.html',
  styleUrl: './gb-presenter-dialog-templates.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbPresenterDialogTemplatesComponent implements OnDestroy {
  _showPreview = false

  selectedTemplate$ = this.projectorTemplatesFacade.selectedProjectorTemplate$
  applyTemplateLoaded$ = this.projectorTemplatesFacade.applyTemplateLoaded$
  editCreateLoaded$ = this.projectorsFacade.editCreateLoaded$
  loaded$ = this.applyTemplateLoaded$.pipe(
    combineLatestWith(this.editCreateLoaded$),
    map(([applyLoaded, editCreateLoaded]) => applyLoaded && editCreateLoaded),
  )

  slideOptions$ = combineLatest([
    this.projectorsFacade.allProjectors$,
    this.projectorContainersFacade.selectedProjectorContainer$,
  ]).pipe(
    map(([projectors, selectedContainer]) => {
      if (!selectedContainer?.uuid) {
        return []
      }

      return projectors
        .filter((projector) => projector.container === selectedContainer.uuid)
        .map((projector) => ({
          uuid: projector.uuid,
          name: projector.name,
        }))
    }),
  )

  targetControl = new FormControl<ApplyTemplateTarget>('existing', {
    nonNullable: true,
    validators: [Validators.required],
  })

  existingSlideUuidControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  newSlideNameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(30)],
  })

  constructor(
    private projectorTemplatesFacade: ProjectorTemplatesFacade,
    private projectorsFacade: ProjectorsFacade,
    private projectorContainersFacade: ProjectorContainersFacade,
  ) {
    this.slideOptions$.pipe(take(1)).subscribe((slides) => {
      this.existingSlideUuidControl.setValue(slides[0]?.uuid || '')
      this.newSlideNameControl.setValue(this.getDefaultSlideName(slides))
    })

    this.targetControl.valueChanges.subscribe((target) => {
      if (target === 'existing') {
        this.newSlideNameControl.clearValidators()
        this.existingSlideUuidControl.setValidators([Validators.required])
      } else {
        this.existingSlideUuidControl.clearValidators()
        this.newSlideNameControl.setValidators([Validators.required, Validators.maxLength(30)])
      }

      this.existingSlideUuidControl.updateValueAndValidity()
      this.newSlideNameControl.updateValueAndValidity()
    })
  }

  ngOnDestroy(): void {
    this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.clearSelectedProjectorTemplate())
  }

  apply(): void {
    if (this.targetControl.value === 'existing' && this.existingSlideUuidControl.value) {
      this.projectorsFacade.dispatch(ProjectorActions.selectProjector({ uuid: this.existingSlideUuidControl.value }))
      this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.applySelectedTemplateToSelectedSlide())
      return
    }

    if (this.targetControl.value === 'create') {
      const slideName = this.newSlideNameControl.value.trim()
      if (!slideName) {
        this.newSlideNameControl.markAsTouched()
        return
      }

      combineLatest([
        this.projectorContainersFacade.selectedProjectorContainer$,
        this.projectorTemplatesFacade.selectedProjectorTemplate$,
      ])
        .pipe(take(1))
        .subscribe(([container, template]) => {
          if (!container?.uuid || !template?.uuid) {
            return
          }

          this.projectorsFacade.dispatch(
            ProjectorActions.addSlide({
              name: slideName,
              containerUuid: container.uuid,
              templateUuid: template.uuid,
            }),
          )
        })
      return
    }

    this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.applySelectedTemplateToSelectedSlide())
  }

  isStepTwoValid(): boolean {
    if (this.targetControl.value === 'existing') {
      return this.existingSlideUuidControl.valid
    }

    return this.newSlideNameControl.valid
  }

  getCanApply(selectedTemplateUuid: string | undefined | null): boolean {
    return !!selectedTemplateUuid && this.targetControl.valid && this.isStepTwoValid()
  }

  previewTemplate(uuid: string) {
    // @TODO GB-11949 - preview dialog
    // this.projectorTemplatesFacade.dispatch(ProjectorTemplatesActions.selectProjectorTemplate({ uuid }))
  }

  private getDefaultSlideName(slides: SlideOption[]): string {
    return `Slide ${slides.length + 1}`
  }
}
